// Adapted from shadcn/ui's Base Nova Select (MIT; docs/design-system/shadcn-LICENSE.md).
import { Select as SelectPrimitive } from '@base-ui/react/select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '@/shared/lib/cn';

import type { ComponentProps, ReactNode } from 'react';

export const Select = SelectPrimitive.Root;
export const SelectValue = SelectPrimitive.Value;

export function SelectTrigger({ className, children, ...props }: SelectPrimitive.Trigger.Props) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn('ui-select-trigger', className)}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon className="ui-select-chevron">
        <ChevronDown size={15} aria-hidden="true" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectContent({ children, ...props }: SelectPrimitive.Popup.Props) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side="bottom"
        align="start"
        sideOffset={6}
        alignItemWithTrigger={false}
        className="ui-select-positioner"
      >
        <SelectPrimitive.Popup className="ui-select-popup" {...props}>
          <SelectPrimitive.ScrollUpArrow className="ui-select-scroll">
            <ChevronUp size={14} aria-hidden="true" />
          </SelectPrimitive.ScrollUpArrow>
          <SelectPrimitive.List className="ui-select-list">{children}</SelectPrimitive.List>
          <SelectPrimitive.ScrollDownArrow className="ui-select-scroll">
            <ChevronDown size={14} aria-hidden="true" />
          </SelectPrimitive.ScrollDownArrow>
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

export function SelectItem({ children, ...props }: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item className="ui-select-item" {...props}>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="ui-select-check">
        <Check size={14} aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

export interface SelectOption {
  value: string | number;
  label: ReactNode;
  disabled?: boolean;
}

/** A labeled, controlled select; the same popup and keyboard behavior on every surface. */
export function SelectField({
  value,
  onValueChange,
  options,
  placeholder = 'Choose an option',
  size = 'default',
  name,
  disabled,
  finalFocus,
  ...triggerProps
}: Pick<
  ComponentProps<'button'>,
  | 'id'
  | 'className'
  | 'aria-label'
  | 'aria-labelledby'
  | 'aria-describedby'
  | 'aria-invalid'
  | 'disabled'
  | 'name'
> & {
  value: string | number;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  size?: 'sm' | 'default';
  finalFocus?: SelectPrimitive.Popup.Props['finalFocus'];
}) {
  const items = options.map((option) => ({ ...option, value: String(option.value) }));
  // An empty value can be a real choice (e.g. system microphone), or a placeholder.
  const selected = value === '' && !items.some((item) => item.value === '') ? null : String(value);
  return (
    <Select
      items={items}
      value={selected}
      disabled={disabled}
      name={name}
      onValueChange={(next) => {
        if (next !== null) onValueChange(next);
      }}
    >
      <SelectTrigger data-size={size} {...triggerProps}>
        <SelectValue className="ui-select-value" placeholder={placeholder}>
          {items.find((item) => item.value === selected)?.label ?? placeholder}
        </SelectValue>
      </SelectTrigger>
      <SelectContent finalFocus={finalFocus}>
        {items.map((item) => (
          <SelectItem key={item.value} value={item.value} disabled={item.disabled}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
